import { MixedSchema, ParentDepsKey } from '@framework/validation';
import { fromPairs } from 'lodash';
import { useCallback, useEffect, useMemo } from 'react';
import { get, useFormContext, useFormState, useWatch } from 'react-hook-form';
import { useFormAugmentedContext } from '../form/augmented/FormAugmentedContext';
import { useFormSectionEnabled } from '../form/section/FormSectionContext';
import { getAugmentedSchema } from './getAugmentedSchema';
import { useAbsoluteDeps } from './useAbsoluteDeps';
import { isFieldEnabled, isFieldLayoutEnabled } from './utils/isFieldEnabled';

type ISetValueOptions = Partial<{
  shouldDirty: boolean;
  shouldValidate: boolean;
  shouldTouch: boolean;
}>;

/**
 * Hook per ritornare i dettagli di un campo di input
 */
export function useField<T>(name: string | string[] | undefined) {
  const { errors, dirtyFields, touchedFields, isSubmitted } = useFormState({
    name,
  });
  const methods = useFormContext();
  const augmented = useFormAugmentedContext();
  const sectionEnabled = useFormSectionEnabled();
  const names = useMemo(
    () => (Array.isArray(name) ? name : name == null ? [] : [name]),
    [name],
  );

  const schema = names.map(n => {
    return getAugmentedSchema(augmented, methods.getValues(), n);
  }) as MixedSchema[];

  // Non è necessario perché `useWatch` utilizza già il memo internamente
  // const deps = useMemo(() => schema.deps, [schema.deps.join(',')]);
  const deps = schema.map(s => [
    ...(s?.deps ?? []),
    ...(s?.spec?.meta?.[ParentDepsKey] ?? []),
  ]);

  const absoluteDeps = useAbsoluteDeps(deps, names);

  const depsValues = useWatch({
    name: absoluteDeps,
    disabled: deps.length === 0,
  });

  // Quando cambia uno dei valori dipendenti, triggera la validazione
  // In questo modo gli errori vengono mostrati (o rimossi) immediatamente
  // quando le condizioni cambiano.
  useEffect(() => {
    // Avvia la validazione solo se ci sono valori nelle dipendenze
    if (depsValues && depsValues.length > 0) {
      methods.trigger(names); // pf.t3
    }
  }, [depsValues]);

  const error = names.map(n => get(errors, n)).filter(e => !!e);
  const touched = names.map(n => get(touchedFields, n)).filter(e => !!e);
  const dirty = names.map(n => get(dirtyFields, n)).filter(e => !!e);

  const setFieldValue = useCallback(
    (value: T, options: ISetValueOptions = { shouldDirty: true }) => {
      if (!name) return;
      if (Array.isArray(name)) {
        for (const n of name) {
          methods.setValue(n, value, options);
        }
      } else {
        methods.setValue(name, value, options);
      }
    },
    [name, methods],
  );

  const getValues = useCallback((): Record<string, any> | undefined => {
    if (!names) return undefined;

    return fromPairs(names.map(n => [n, methods.getValues(n)]));
  }, [name, methods.getValues]);

  const getValue = useCallback(<R extends T>(): R | undefined => {
    if (!name) return undefined;
    if (Array.isArray(name)) {
      console.warn(`[useField] Impossibile recuperare valore da campi multipli. Utilizzare getValues() invece.`, {name}); // prettier-ignore
      return undefined;
    }
    return methods.getValues(name);
  }, [name, methods.getValues]);

  const unregister = useCallback(() => {
    if (!name) return;
    methods.unregister(name);
  }, [methods.unregister, name]);

  /**
   * Ottiene lo schema aggiornato con i valori attuali del form, per il campo
   * indicato. Lo ottiene solo per il primo nome, se sono presenti più nomi
   */
  const getActualSchema = useCallback(() => {
    const actualSchema = getAugmentedSchema(
      augmented,
      methods.getValues(),
      names[0],
    ) as MixedSchema | null;
    return actualSchema;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [augmented, methods.getValues, names]);

  return {
    error,
    dirty,
    touched,
    isSubmitted,
    schema,
    getActualSchema,
    visible: !schema.some(s => s?.spec?.strip),
    /** È editabile solo se tutti gli schemi dicono che è editabile (true or undefined)*/
    enabled: sectionEnabled === false ? false : isFieldEnabled(schema),
    /** Indica se il campo è editabile, ma viene utilizzato solo dai componenti a livello di UI/UX e non ha impatto sulla validazione (e.g. permette modifiche) */
    layoutEnabled:
      sectionEnabled === false ? false : isFieldLayoutEnabled(schema),
    /** Imposta il valore del campo a `value`. Non funziona quando l'hook è utilizzato su campi multipli. */
    setValue: setFieldValue,
    /** Ritorna il valore del campo indicato. Per recuperare campi multipli, utilizzare getValues().*/
    getValue,
    /** Ritorna i valore di tutti i nomi passati come campi */
    getValues,
    unregister,
  };
}

export type IUseField = ReturnType<typeof useField>;
